import { useCallback, useEffect, useRef, useState } from 'react';
import { FIELD_LENGTH_M, FIELD_WIDTH_M } from '@4cm/core/tactics';
import { MovementArrow2D, type MovementArrow2DSpec } from './MovementArrow2D';

/**
 * Pointer-down sketcher for the 2D tactics board's arrow draw mode.
 *
 * Sits as an invisible `<rect>` covering the pitch (slightly oversized
 * to allow arrows that start on the touchline). When `active`, pointer
 * events sample the cursor at ~0.6m intervals to build a polyline,
 * mirroring the MIN_SEGMENT_DIST in `TacticsBoard3D.ArrowDrawLayer`.
 *
 * If `seedOrigin` is set (i.e. the coach pressed on a player token
 * to start an arrow), we bootstrap the draw state immediately so the
 * very next pointer move on the capture rect extends the curve from
 * the player's position.
 */
export interface ArrowDrawLayer2DProps {
  active: boolean;
  color: string;
  seedOrigin: { x: number; z: number } | null;
  onSeedConsumed: () => void;
  onArrowComplete: (points: Array<{ x: number; z: number }>) => void;
  onDragChange: (dragging: boolean) => void;
  /** Parent SVG ref so we can project pointer events into world metres. */
  svgRef: React.RefObject<SVGSVGElement | null>;
}

const HALF_W = FIELD_WIDTH_M / 2;
const HALF_L = FIELD_LENGTH_M / 2;
const PADDING = 5;        // a bit of margin so arrows can start on the touchline
const MIN_SEGMENT_DIST = 0.6;

function projectPointer(
  svg: SVGSVGElement | null,
  e: { clientX: number; clientY: number },
): { x: number; z: number } | null {
  if (!svg) return null;
  const ctm =
    typeof svg.getScreenCTM === 'function' ? svg.getScreenCTM() : null;
  if (!ctm) return null;
  const pt =
    typeof svg.createSVGPoint === 'function'
      ? svg.createSVGPoint()
      : new DOMPoint();
  pt.x = e.clientX;
  pt.y = e.clientY;
  const inverse = ctm.inverse();
  const transformed =
    'matrixTransform' in pt && typeof (pt as SVGPoint).matrixTransform === 'function'
      ? (pt as SVGPoint).matrixTransform(inverse)
      : (pt as DOMPoint).matrixTransform(inverse);
  // Landscape mapping: SVG (x_svg, y_svg) → world (y_svg, -x_svg).
  return { x: transformed.y, z: -transformed.x };
}

export function ArrowDrawLayer2D({
  active,
  color,
  seedOrigin,
  onSeedConsumed,
  onArrowComplete,
  onDragChange,
  svgRef,
}: ArrowDrawLayer2DProps) {
  const drawingRef = useRef(false);
  const pointsRef = useRef<Array<{ x: number; z: number }>>([]);
  const [preview, setPreview] = useState<MovementArrow2DSpec | null>(null);

  // If the coach pressed on a player token in draw mode, seed the curve
  // with that origin so the next pointer move extends from there.
  useEffect(() => {
    if (!active || !seedOrigin) return;
    drawingRef.current = true;
    pointsRef.current = [seedOrigin];
    setPreview({ id: 'preview', points: [seedOrigin], color });
    onDragChange(true);
    onSeedConsumed();
  }, [active, seedOrigin, color, onDragChange, onSeedConsumed]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!active) return;
      e.stopPropagation();
      const next = projectPointer(svgRef.current, e);
      if (!next) return;
      // Already seeded by a player token — append the cursor as the
      // second control point and continue the curve.
      if (drawingRef.current) {
        pointsRef.current = [...pointsRef.current, next];
        setPreview({ id: 'preview', points: pointsRef.current, color });
        (e.target as Element).setPointerCapture?.(e.pointerId);
        return;
      }
      drawingRef.current = true;
      pointsRef.current = [next];
      setPreview({ id: 'preview', points: [next], color });
      onDragChange(true);
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [active, color, onDragChange, svgRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!active || !drawingRef.current) return;
      e.stopPropagation();
      const next = projectPointer(svgRef.current, e);
      if (!next) return;
      const last = pointsRef.current[pointsRef.current.length - 1];
      const d = Math.hypot(next.x - last.x, next.z - last.z);
      if (d < MIN_SEGMENT_DIST) return;
      pointsRef.current = [...pointsRef.current, next];
      setPreview({ id: 'preview', points: pointsRef.current, color });
    },
    [active, color, svgRef],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<SVGRectElement>) => {
      if (!active || !drawingRef.current) return;
      e.stopPropagation();
      drawingRef.current = false;
      onDragChange(false);
      (e.target as Element).releasePointerCapture?.(e.pointerId);
      const final = pointsRef.current;
      pointsRef.current = [];
      setPreview(null);
      if (final.length >= 2) onArrowComplete(final);
    },
    [active, onArrowComplete, onDragChange],
  );

  if (!active) return preview ? <MovementArrow2D arrow={preview} reduced /> : null;

  return (
    <>
      {/* Landscape pitch: SVG-x spans the length, SVG-y spans the width. */}
      <rect
        x={-HALF_L - PADDING}
        y={-HALF_W - PADDING}
        width={FIELD_LENGTH_M + PADDING * 2}
        height={FIELD_WIDTH_M + PADDING * 2}
        fill="transparent"
        pointerEvents="all"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="t2d-arrow-draw-layer"
      />
      {preview && <MovementArrow2D arrow={preview} reduced />}
    </>
  );
}
